<template>
  <section class="points-mall-panel">
    <div class="summary-grid">
      <article v-for="stat in stats" :key="stat.label" class="summary-card">
        <div>
          <span>{{ stat.label }}</span>
          <strong>{{ stat.value }}</strong>
        </div>
        <small :class="`is-${stat.tone}`">{{ stat.sub }}</small>
      </article>
    </div>

    <div class="panel-tabs">
      <button :class="{ 'is-active': activePanel === 'items' }" @click="activePanel = 'items'">兑换项</button>
      <button :class="{ 'is-active': activePanel === 'exchanges' }" @click="activePanel = 'exchanges'">兑换记录</button>
    </div>

    <article v-show="activePanel === 'items'" class="content-card">
      <div class="filter-bar">
        <el-input v-model.trim="itemFilters.keyword" clearable placeholder="搜索兑换项名称/描述" @keyup.enter="searchItems" />
        <el-select v-model="itemFilters.type" clearable placeholder="全部类型">
          <el-option label="优惠券" value="COUPON" />
          <el-option label="实物" value="PHYSICAL" />
        </el-select>
        <el-select v-model="itemFilters.status" clearable placeholder="全部状态">
          <el-option label="上架" value="ON_SHELF" />
          <el-option label="下架" value="OFF_SHELF" />
        </el-select>
        <div class="filter-bar__actions">
          <el-button type="primary" @click="searchItems">搜索</el-button>
          <el-button @click="resetItemFilters">重置</el-button>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>兑换项名称</th>
              <th>类型</th>
              <th class="is-right">所需积分</th>
              <th>兑换内容</th>
              <th class="is-right">库存</th>
              <th>限制</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in itemRows" :key="item.id">
              <td><span class="cell-text">{{ item.name }}</span></td>
              <td>{{ getItemTypeText(item.type) }}</td>
              <td class="is-right">{{ item.points }}</td>
              <td><span class="cell-text">{{ getRewardText(item) }}</span></td>
              <td class="is-right">{{ item.stock }}</td>
              <td>{{ item.limitPerUser > 0 ? `每人 ${item.limitPerUser} 次` : '不限' }}</td>
              <td><span :class="['status-tag', item.status === 'ON_SHELF' ? 'is-success' : 'is-muted']">{{ getItemStatusText(item.status) }}</span></td>
              <td class="is-right">
                <div class="row-actions">
                  <button title="查看详情" @click="openItemViewer(item)"><el-icon><View /></el-icon></button>
                  <button title="编辑" @click="openItemEditor(item)"><el-icon><EditPen /></el-icon></button>
                  <button title="删除" @click="removeItem(item)"><el-icon><Delete /></el-icon></button>
                </div>
              </td>
            </tr>
            <tr v-if="!itemRows.length && !loadingItems">
              <td colspan="8"><el-empty description="暂无积分商城兑换项" /></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="itemPage" class="table-footer">
        <span>共 {{ itemPage.total }} 条记录，第 {{ itemPage.page }}/{{ itemTotalPages }} 页</span>
        <div>
          <el-button :disabled="itemPage.page <= 1" @click="changeItemPage(itemPage.page - 1)">上一页</el-button>
          <el-button type="primary" plain>{{ itemPage.page }}</el-button>
          <el-button :disabled="!itemPage.hasMore" @click="changeItemPage(itemPage.page + 1)">下一页</el-button>
        </div>
      </div>
    </article>

    <article v-show="activePanel === 'exchanges'" class="content-card">
      <div class="filter-bar">
        <el-input v-model.trim="exchangeFilters.keyword" clearable placeholder="搜索兑换项/用户昵称/手机号" @keyup.enter="searchExchanges" />
        <el-select v-model="exchangeFilters.type" clearable placeholder="全部类型">
          <el-option label="优惠券" value="COUPON" />
          <el-option label="实物" value="PHYSICAL" />
        </el-select>
        <el-select v-model="exchangeFilters.status" clearable placeholder="全部状态">
          <el-option label="已完成" value="COMPLETED" />
          <el-option label="待领取" value="PENDING_PICKUP" />
          <el-option label="已领取" value="PICKED_UP" />
          <el-option label="已取消" value="CANCELLED" />
        </el-select>
        <div class="filter-bar__actions">
          <el-button type="primary" @click="searchExchanges">搜索记录</el-button>
          <el-button @click="resetExchangeFilters">重置</el-button>
        </div>
      </div>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>兑换项</th>
              <th>用户</th>
              <th>类型</th>
              <th class="is-right">消耗积分</th>
              <th>领取码</th>
              <th>兑换时间</th>
              <th>状态</th>
              <th class="is-right">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="record in exchangeRows" :key="record.id">
              <td><span class="cell-text">{{ record.itemName }}</span></td>
              <td><span class="cell-text">{{ record.nickname || record.phone || '--' }}</span></td>
              <td>{{ getItemTypeText(record.type) }}</td>
              <td class="is-right">{{ record.pointsCost }}</td>
              <td>{{ record.pickupCode || '--' }}</td>
              <td>{{ record.createdAt || '--' }}</td>
              <td><span :class="['status-tag', exchangeStatusTone(record.status)]">{{ getExchangeStatusText(record.status) }}</span></td>
              <td class="is-right">
                <el-button
                  v-if="record.status === 'PENDING_PICKUP'"
                  size="small"
                  type="primary"
                  plain
                  @click="pickupExchange(record)"
                >
                  核销
                </el-button>
                <span v-else>--</span>
              </td>
            </tr>
            <tr v-if="!exchangeRows.length && !loadingExchanges">
              <td colspan="8"><el-empty description="暂无兑换记录" /></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="exchangePage" class="table-footer">
        <span>共 {{ exchangePage.total }} 条记录，第 {{ exchangePage.page }}/{{ exchangeTotalPages }} 页</span>
        <div>
          <el-button :disabled="exchangePage.page <= 1" @click="changeExchangePage(exchangePage.page - 1)">上一页</el-button>
          <el-button type="primary" plain>{{ exchangePage.page }}</el-button>
          <el-button :disabled="!exchangePage.hasMore" @click="changeExchangePage(exchangePage.page + 1)">下一页</el-button>
        </div>
      </div>
    </article>

    <el-dialog v-model="itemEditorVisible" :title="itemEditorTitle" width="720px">
      <div class="detail-grid detail-grid--single">
        <article class="detail-card">
          <div class="detail-actions">
            <GuidedField label="兑换项名称" hint="用户在小程序积分商城看到的名称。" required>
              <el-input v-model.trim="itemForm.name" :disabled="isItemViewMode" placeholder="如 饮品满20减5券" />
            </GuidedField>
            <GuidedField label="兑换类型" hint="优惠券会进入用户卡包，实物用于门店领取。">
              <el-select v-model="itemForm.type" :disabled="isItemViewMode">
                <el-option label="优惠券" value="COUPON" />
                <el-option label="实物" value="PHYSICAL" />
              </el-select>
            </GuidedField>
            <GuidedField v-if="itemForm.type === 'COUPON'" label="优惠券模板" hint="兑换成功后自动发放到用户卡包。" required>
              <el-select v-model="itemForm.couponTemplateId" :disabled="isItemViewMode" filterable placeholder="请选择优惠券模板">
                <el-option v-for="coupon in couponOptions" :key="coupon.id" :label="coupon.name" :value="coupon.id" />
              </el-select>
            </GuidedField>
            <GuidedField v-if="itemForm.type === 'PHYSICAL'" label="实物图片" hint="小程序积分商城展示的实物主图。" wide required>
              <LocalImagePicker v-if="!isItemViewMode" v-model="itemForm.image" button-text="上传实物图片" />
              <img v-else-if="itemForm.image" class="physical-preview" :src="getImageUrl(itemForm.image)" alt="实物图片">
              <span v-else class="muted-text">未上传图片</span>
            </GuidedField>
            <GuidedField label="所需积分" hint="兑换时从用户账户扣减的积分数。">
              <el-input-number v-model="itemForm.points" :disabled="isItemViewMode" :min="0" controls-position="right" />
            </GuidedField>
            <GuidedField label="兑换内容" hint="优惠券展示说明、门店领取说明或实物描述。">
              <el-input v-model.trim="itemForm.description" :disabled="isItemViewMode" placeholder="如 满 20 减 5" />
            </GuidedField>
            <GuidedField label="库存" hint="剩余可兑换数量，售罄后用户端不可兑换。">
              <el-input-number v-model="itemForm.stock" :disabled="isItemViewMode" :min="0" controls-position="right" />
            </GuidedField>
            <GuidedField label="每人兑换上限" hint="0 表示不限。">
              <el-input-number v-model="itemForm.limitPerUser" :disabled="isItemViewMode" :min="0" controls-position="right" />
            </GuidedField>
            <GuidedField v-if="itemForm.type === 'PHYSICAL'" label="领取说明" hint="展示给用户和门店核销人员。">
              <el-input v-model.trim="itemForm.pickupDesc" :disabled="isItemViewMode" placeholder="如 兑换后 7 天内到门店领取" />
            </GuidedField>
            <GuidedField label="标签" hint="如 热门、周边、新品，可选。">
              <el-input v-model.trim="itemForm.badge" :disabled="isItemViewMode" placeholder="如 热门" />
            </GuidedField>
            <GuidedField label="排序" hint="数值越小越靠前。">
              <el-input-number v-model="itemForm.sort" :disabled="isItemViewMode" :min="0" controls-position="right" />
            </GuidedField>
            <GuidedField label="上架状态" hint="下架后小程序端不会展示或不可兑换。">
              <el-select v-model="itemForm.status" :disabled="isItemViewMode">
                <el-option label="上架" value="ON_SHELF" />
                <el-option label="下架" value="OFF_SHELF" />
              </el-select>
            </GuidedField>
          </div>
        </article>
      </div>
      <template #footer>
        <el-button @click="itemEditorVisible = false">{{ isItemViewMode ? '关闭' : '取消' }}</el-button>
        <el-button v-if="!isItemViewMode" type="primary" :loading="itemSubmitting" @click="submitItem">保存</el-button>
      </template>
    </el-dialog>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { Delete, EditPen, View } from '@element-plus/icons-vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import GuidedField from '@/components/GuidedField.vue'
import LocalImagePicker from '@/components/LocalImagePicker.vue'
import { getImageUrl } from '@/api/upload'
import { fetchCoupons, type AdminCoupon } from '@/api/marketing'
import {
  createPointsMallItem,
  deletePointsMallItem,
  fetchPointsMallExchanges,
  fetchPointsMallItems,
  pickupPointsMallExchange,
  updatePointsMallItem,
  type AdminPointsMallExchange,
  type AdminPointsMallItem,
  type PointsMallExchangeListParams,
  type PointsMallExchangeStatus,
  type PointsMallItemListParams,
  type PointsMallItemSavePayload,
  type PointsMallItemStatus,
  type PointsMallItemType,
} from '@/api/points-mall'

type StatTone = 'brand' | 'success' | 'warning' | 'error'

const loadingItems = ref(false)
const loadingExchanges = ref(false)
const itemSubmitting = ref(false)
const itemEditorVisible = ref(false)
const activePanel = ref<'items' | 'exchanges'>('items')
const itemEditorMode = ref<'create' | 'edit' | 'view'>('create')
const itemPage = ref<Awaited<ReturnType<typeof fetchPointsMallItems>>>()
const exchangePage = ref<Awaited<ReturnType<typeof fetchPointsMallExchanges>>>()
const couponOptions = ref<AdminCoupon[]>([])

const itemFilters = reactive<PointsMallItemListParams>({
  page: 1,
  pageSize: 10,
  keyword: '',
  type: undefined,
  status: undefined,
})
const exchangeFilters = reactive<PointsMallExchangeListParams>({
  page: 1,
  pageSize: 10,
  keyword: '',
  type: undefined,
  status: undefined,
})
const itemForm = reactive<PointsMallItemSavePayload>({
  id: undefined,
  type: 'COUPON',
  name: '',
  description: '',
  image: '',
  points: 0,
  stock: 0,
  limitPerUser: 0,
  couponTemplateId: '',
  pickupType: 'STORE_PICKUP',
  pickupDesc: '',
  badge: '',
  sort: 0,
  status: 'ON_SHELF',
})

const itemRows = computed(() => itemPage.value?.list ?? [])
const exchangeRows = computed(() => exchangePage.value?.list ?? [])
const itemTotalPages = computed(() => !itemPage.value ? 1 : Math.max(1, Math.ceil(itemPage.value.total / itemPage.value.pageSize)))
const exchangeTotalPages = computed(() => !exchangePage.value ? 1 : Math.max(1, Math.ceil(exchangePage.value.total / exchangePage.value.pageSize)))
const isItemViewMode = computed(() => itemEditorMode.value === 'view')
const itemEditorTitle = computed(() => {
  if (itemEditorMode.value === 'view') return '查看兑换项'
  return itemEditorMode.value === 'edit' ? '编辑兑换项' : '新增兑换项'
})
const stats = computed<Array<{ label: string; value: string; sub: string; tone: StatTone }>>(() => {
  const rows = itemRows.value
  const onShelf = rows.filter((item) => item.status === 'ON_SHELF')
  const couponCount = onShelf.filter((item) => item.type === 'COUPON').length
  const physicalCount = onShelf.filter((item) => item.type === 'PHYSICAL').length
  const lowStockPhysical = rows.filter((item) => item.type === 'PHYSICAL' && item.stock <= 10).length
  const consumedPoints = exchangeRows.value.reduce((total, item) => total + item.pointsCost, 0)

  return [
    { label: '上架兑换项', value: String(onShelf.length), sub: `优惠券 ${couponCount} / 实物 ${physicalCount}`, tone: 'brand' },
    { label: '兑换记录', value: String(exchangePage.value?.total ?? 0), sub: '后端实时分页', tone: 'success' },
    { label: '低库存实物', value: String(lowStockPhysical), sub: '库存不高于 10', tone: 'warning' },
    { label: '当前页消耗积分', value: consumedPoints.toLocaleString(), sub: '按兑换记录统计', tone: 'success' },
  ]
})

onMounted(() => {
  void Promise.all([loadItems(), loadExchanges(), loadCouponOptions()])
})

async function loadItems(): Promise<void> {
  loadingItems.value = true
  try {
    itemPage.value = await fetchPointsMallItems(normalizeItemFilters())
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '积分商城兑换项加载失败')
  } finally {
    loadingItems.value = false
  }
}

async function loadExchanges(): Promise<void> {
  loadingExchanges.value = true
  try {
    exchangePage.value = await fetchPointsMallExchanges(normalizeExchangeFilters())
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '积分商城兑换记录加载失败')
  } finally {
    loadingExchanges.value = false
  }
}

async function loadCouponOptions(): Promise<void> {
  try {
    const page = await fetchCoupons({ page: 1, pageSize: 100 })
    couponOptions.value = page.list
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '优惠券模板加载失败')
  }
}

async function searchItems(): Promise<void> {
  itemFilters.page = 1
  await loadItems()
}

async function resetItemFilters(): Promise<void> {
  Object.assign(itemFilters, { page: 1, pageSize: 10, keyword: '', type: undefined, status: undefined })
  await loadItems()
}

async function searchExchanges(): Promise<void> {
  exchangeFilters.page = 1
  await loadExchanges()
}

async function resetExchangeFilters(): Promise<void> {
  Object.assign(exchangeFilters, { page: 1, pageSize: 10, keyword: '', type: undefined, status: undefined })
  await loadExchanges()
}

async function changeItemPage(page: number): Promise<void> {
  itemFilters.page = page
  await loadItems()
}

async function changeExchangePage(page: number): Promise<void> {
  exchangeFilters.page = page
  await loadExchanges()
}

function openCreateItem(): void {
  itemEditorMode.value = 'create'
  Object.assign(itemForm, {
    id: undefined,
    type: 'COUPON',
    name: '',
    description: '',
    image: '',
    points: 0,
    stock: 0,
    limitPerUser: 1,
    couponTemplateId: '',
    pickupType: 'STORE_PICKUP',
    pickupDesc: '',
    badge: '',
    sort: 0,
    status: 'ON_SHELF',
  })
  itemEditorVisible.value = true
}

function openItemEditor(item: AdminPointsMallItem): void {
  fillItemForm(item, 'edit')
}

function openItemViewer(item: AdminPointsMallItem): void {
  fillItemForm(item, 'view')
}

function fillItemForm(item: AdminPointsMallItem, mode: 'edit' | 'view'): void {
  itemEditorMode.value = mode
  Object.assign(itemForm, {
    id: item.id,
    type: item.type,
    name: item.name,
    description: item.description || '',
    image: item.image || '',
    points: item.points,
    stock: item.stock,
    limitPerUser: item.limitPerUser,
    couponTemplateId: item.couponTemplateId || '',
    pickupType: item.pickupType || 'STORE_PICKUP',
    pickupDesc: item.pickupDesc || '',
    badge: item.badge || '',
    sort: item.sort,
    status: item.status,
  })
  itemEditorVisible.value = true
}

async function submitItem(): Promise<void> {
  const payload = normalizeItemPayload()
  if (!payload) return

  itemSubmitting.value = true
  try {
    if (itemEditorMode.value === 'edit') {
      await updatePointsMallItem(payload)
      ElMessage.success('兑换项已更新')
    } else {
      delete payload.id
      await createPointsMallItem(payload)
      ElMessage.success('兑换项已创建')
    }
    itemEditorVisible.value = false
    await loadItems()
  } catch (error) {
    ElMessage.error(error instanceof Error ? error.message : '兑换项保存失败')
  } finally {
    itemSubmitting.value = false
  }
}

async function removeItem(item: AdminPointsMallItem): Promise<void> {
  await ElMessageBox.confirm(`确定删除兑换项“${item.name}”吗？删除后不可恢复。`, '删除兑换项', { type: 'warning' })
  await deletePointsMallItem(item.id)
  ElMessage.success('兑换项已删除')
  await loadItems()
}

async function pickupExchange(record: AdminPointsMallExchange): Promise<void> {
  const { value } = await ElMessageBox.prompt(`确认核销领取码 ${record.pickupCode || record.id}？`, '核销实物领取', {
    confirmButtonText: '确认核销',
    cancelButtonText: '取消',
    inputPlaceholder: '核销备注，可选',
  })
  await pickupPointsMallExchange(record.id, { pickupRemark: value })
  ElMessage.success('实物领取已核销')
  await loadExchanges()
}

function normalizeItemPayload(): PointsMallItemSavePayload | undefined {
  if (!itemForm.name.trim()) {
    ElMessage.warning('请填写兑换项名称')
    return undefined
  }
  if (itemForm.type === 'COUPON' && !itemForm.couponTemplateId?.trim()) {
    ElMessage.warning('请选择优惠券模板')
    return undefined
  }
  if (itemForm.type === 'PHYSICAL' && !itemForm.image?.trim()) {
    ElMessage.warning('请先上传实物图片')
    return undefined
  }

  return {
    id: itemForm.id,
    type: itemForm.type,
    name: itemForm.name.trim(),
    description: itemForm.description?.trim() || undefined,
    image: itemForm.type === 'PHYSICAL' ? itemForm.image?.trim() : undefined,
    points: Number(itemForm.points ?? 0),
    stock: Number(itemForm.stock ?? 0),
    limitPerUser: Number(itemForm.limitPerUser ?? 0),
    couponTemplateId: itemForm.type === 'COUPON' ? itemForm.couponTemplateId?.trim() : undefined,
    pickupType: itemForm.type === 'PHYSICAL' ? itemForm.pickupType || 'STORE_PICKUP' : undefined,
    pickupDesc: itemForm.type === 'PHYSICAL' ? itemForm.pickupDesc?.trim() || undefined : undefined,
    badge: itemForm.badge?.trim() || undefined,
    sort: Number(itemForm.sort ?? 0),
    status: itemForm.status,
  }
}

function normalizeItemFilters(): PointsMallItemListParams {
  return {
    page: itemFilters.page,
    pageSize: itemFilters.pageSize,
    keyword: itemFilters.keyword?.trim() || undefined,
    type: itemFilters.type || undefined,
    status: itemFilters.status || undefined,
  }
}

function normalizeExchangeFilters(): PointsMallExchangeListParams {
  return {
    page: exchangeFilters.page,
    pageSize: exchangeFilters.pageSize,
    keyword: exchangeFilters.keyword?.trim() || undefined,
    type: exchangeFilters.type || undefined,
    status: exchangeFilters.status || undefined,
  }
}

function getItemTypeText(type: PointsMallItemType): string {
  return type === 'COUPON' ? '优惠券' : '实物'
}

function getItemStatusText(status: PointsMallItemStatus): string {
  return status === 'ON_SHELF' ? '上架' : '下架'
}

function getExchangeStatusText(status: PointsMallExchangeStatus): string {
  const map: Record<PointsMallExchangeStatus, string> = {
    COMPLETED: '已完成',
    PENDING_PICKUP: '待领取',
    PICKED_UP: '已领取',
    CANCELLED: '已取消',
  }
  return map[status] ?? status
}

function exchangeStatusTone(status: PointsMallExchangeStatus): string {
  if (status === 'COMPLETED' || status === 'PICKED_UP') return 'is-success'
  if (status === 'PENDING_PICKUP') return 'is-warning'
  return 'is-muted'
}

function getRewardText(item: AdminPointsMallItem): string {
  if (item.type === 'COUPON') return item.couponTemplateName || item.description || item.couponTemplateId || '--'
  return item.pickupDesc || item.description || '--'
}

defineExpose({
  openCreateItem,
  loadItems,
  loadExchanges,
})
</script>

<style scoped lang="scss">
.points-mall-panel { display: flex; flex-direction: column; gap: 16px; }
.summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.summary-card { min-height: 102px; padding: 20px; border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; display: flex; align-items: center; justify-content: space-between; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.summary-card span { display: block; margin-bottom: 8px; color: var(--jf-color-text-secondary); font-size: 14px; }
.summary-card strong { color: var(--jf-color-text-primary); font-size: 26px; line-height: 1.1; }
.summary-card small { padding: 4px 8px; border-radius: 6px; background: var(--jf-color-background); font-size: 12px; white-space: nowrap; }
.panel-tabs { display: inline-flex; width: fit-content; padding: 4px; border: 1px solid var(--jf-color-border); border-radius: 8px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); }
.panel-tabs button { min-width: 96px; height: 34px; padding: 0 16px; border-radius: 6px; background: transparent; color: var(--jf-color-text-secondary); font-weight: 700; }
.panel-tabs button.is-active { background: var(--jf-color-primary); color: #fff; }
.content-card { border: 1px solid var(--jf-color-border); border-radius: 12px; background: #fff; box-shadow: 0 2px 10px rgba(37, 40, 43, .04); overflow: hidden; }
.filter-bar { min-height: 66px; padding: 14px 16px; border-bottom: 1px solid var(--jf-color-border); display: grid; grid-template-columns: minmax(260px, 1fr) 160px 160px auto; gap: 12px; align-items: center; }
.filter-bar__actions { display: flex; gap: 8px; }
.table-wrap { width: 100%; overflow-x: auto; }
table { width: 100%; border-collapse: collapse; white-space: nowrap; font-size: 14px; }
thead { background: rgba(252, 250, 246, .72); color: var(--jf-color-text-secondary); font-size: 12px; }
th, td { padding: 14px 16px; text-align: left; border-bottom: 1px solid var(--jf-color-border); }
th { font-weight: 700; }
tbody tr:hover { background: rgba(252, 250, 246, .75); }
button { border: 0; cursor: pointer; font: inherit; }
.is-right { text-align: right; }
.cell-text { display: inline-block; max-width: 220px; overflow: hidden; text-overflow: ellipsis; vertical-align: middle; }
.status-tag { display: inline-flex; align-items: center; min-height: 24px; padding: 3px 9px; border-radius: 999px; font-size: 12px; font-weight: 700; }
.is-brand { background: rgba(118, 183, 232, .14); color: var(--jf-color-primary-dark); }
.is-success { background: rgba(69, 185, 124, .12); color: var(--jf-color-success); }
.is-warning { background: rgba(242, 169, 80, .14); color: var(--jf-color-warning); }
.is-muted { background: #f1f3f5; color: var(--jf-color-text-secondary); }
.row-actions { display: inline-flex; justify-content: flex-end; gap: 6px; color: var(--jf-color-primary-dark); }
.row-actions button { width: 28px; height: 28px; border-radius: 6px; display: grid; place-items: center; background: transparent; color: inherit; }
.row-actions button:hover { background: rgba(179, 218, 247, .3); }
.table-footer { padding: 14px 16px; display: flex; align-items: center; justify-content: space-between; gap: 16px; color: var(--jf-color-text-secondary); font-size: 14px; }
.table-footer div { display: flex; gap: 6px; }
.physical-preview { width: 160px; height: 90px; border: 1px solid var(--jf-color-border); border-radius: 8px; object-fit: cover; }
.muted-text { color: var(--jf-color-text-secondary); font-size: 14px; }
@media (max-width: 1180px) {
  .summary-grid { grid-template-columns: 1fr; }
  .filter-bar { grid-template-columns: 1fr; }
}
</style>
